'use client';

import { useState, type MouseEvent } from 'react';
import { IconButton, Menu, MenuItem, Tooltip } from '@/primitives';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { FilterIcon } from '@/primitives/icons/FilterIcon';
import { FilterPlusIcon } from '@/primitives/icons/FilterPlusIcon';

type CardFilterActionsProps = {
  field: string;
  filterValue: string;
  /** Phones collapse the pair into one overflow menu; wider screens keep both. */
  isMobile: boolean;
  onQuickFilter: (field: string, filterValue: string) => void;
  onAddFilter: (field: string, filterValue: string) => void;
};

const iconButtonSx = {
  p: 0.5,
  '&:hover': {
    backgroundColor: 'action.hover',
  },
} as const;

/** Card rows sit inside a link, so every action has to swallow the click. */
function swallow(event: MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * Set-filter / add-filter actions on a search result card row.
 *
 * On wider screens both icons show on hover, as they always have. A phone has
 * no hover, so the pair used to sit on every row permanently and cost ~64px of
 * a 301px column — which is what pushed long sire and dam names into an
 * ellipsis (ILRX-4700). There they collapse into a single overflow menu.
 */
export function CardFilterActions({
  field,
  filterValue,
  isMobile,
  onQuickFilter,
  onAddFilter,
}: CardFilterActionsProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const close = () => setAnchorEl(null);
  const choose =
    (apply: (field: string, filterValue: string) => void) =>
    (event: MouseEvent) => {
      swallow(event);
      close();
      apply(field, filterValue);
    };

  if (!isMobile) {
    return (
      <>
        <Tooltip title="Set filter" placement="top">
          <IconButton
            size="small"
            className="quick-filter-icon"
            aria-label="Set filter"
            onClick={choose(onQuickFilter)}
            sx={iconButtonSx}
          >
            <FilterIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add filter" placement="top">
          <IconButton
            size="small"
            className="quick-filter-icon"
            aria-label="Add filter"
            onClick={choose(onAddFilter)}
            sx={iconButtonSx}
          >
            <FilterPlusIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </>
    );
  }

  return (
    <>
      <IconButton
        size="small"
        className="quick-filter-icon"
        aria-label={`Filter options for ${filterValue}`}
        onClick={(event) => {
          swallow(event);
          setAnchorEl(event.currentTarget);
        }}
        sx={iconButtonSx}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={anchorEl != null}
        onClose={close}
        onClick={(event) => event.stopPropagation()}
      >
        <MenuItem onClick={choose(onQuickFilter)}>Set filter</MenuItem>
        <MenuItem onClick={choose(onAddFilter)}>Add filter</MenuItem>
      </Menu>
    </>
  );
}
